/**
 * LETÖLTÉS ÉS NYOMTATÁS — a már betöltött bájtokból, nem újabb kéréssel.
 *
 * MIÉRT A BLOB, és nem az aláírt cím. A cím öt percig él: aki egy bizonylaton
 * tíz percig dolgozik, és utána nyomtatna, 401-et kapna. A bájtok viszont
 * a nézőben már megvannak — a néző maga is ezekből rajzol.
 *
 * MIÉRT NEM A SAJÁT RAJZUNKAT NYOMTATJUK. A canvas a képernyő felbontásán
 * áll; nyomtatva a számla apró betűi elmosódnának. A blob a böngésző saját
 * nézőjében nyílik meg, és az a papír eredeti felbontásán nyomtat.
 */

/** A fájl a feltöltéskori nevén kerül a könyvelő gépére. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.append(link);
  link.click();
  link.remove();
  // A KATTINTÁS UTÁN visszük el a címet, nem azonnal: a letöltést a böngésző
  // aszinkron indítja, és egy túl korán visszavont cím üres fájlt adna.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * MEDDIG ÁLL A REJTETT KERET, ha a böngésző nem szól, hogy a nyomtatás véget
 * ért. A Chrome PDF-nézője `afterprint`-et nem küld; egy perc kevés volt —
 * a nyomtatóválasztás közben eltűnt volna alóla az előnézet.
 */
export const PRINT_FRAME_MAX_MS = 10 * 60_000;

/**
 * NYOMTATÁS egy rejtett keretből. A keret a blobot a böngésző saját
 * nézőjével tölti be (PDF-nél a PDF-néző, képnél a képnézet), és annak a
 * nyomtatását hívja — így a nyomtatási előnézet ugyanaz, mint bárhol máshol.
 *
 * ⚠️ A TÍPUST A HÍVÓ ADJA, és az a már ellenőrzött PDF/kép (`embedType`). A
 * szerver a fájlt `sandbox` CSP-vel szolgálja, hogy egy HTML vagy SVG soha ne
 * fusson az admin munkamenetével; a blob viszont az admin originjén nyílik
 * meg, a fejlécek nélkül. Az újracímkézés a második védővonal a beágyazható
 * típusok szűrője mellett (code review, 10-16).
 */
export function printBlob(blob: Blob, type: string): void {
  const url = URL.createObjectURL(new Blob([blob], { type }));
  const frame = document.createElement('iframe');
  // A KÉP EGY LAPRA kerül (#68). Magát a képfájlt betöltve a böngésző a
  // kép TERMÉSZETES méretében nyomtat: egy telefonos fotó (~3000 × 4000 px)
  // három lapra lógott ki (élesben, 10-19). A PDF marad a saját nézőjében.
  if (type.startsWith('image/')) {
    frame.srcdoc = imagePrintPage(url);
  }
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  frame.style.position = 'fixed';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.style.right = '0';
  frame.style.bottom = '0';

  const cleanup = () => {
    frame.remove();
    URL.revokeObjectURL(url);
  };

  frame.addEventListener('load', () => {
    const view = frame.contentWindow;
    if (view === null) {
      cleanup();
      return;
    }
    try {
      view.addEventListener('afterprint', cleanup, { once: true });
      view.focus();
      view.print();
      // A PDF-NÉZŐ nem mindig küld `afterprint`-et (a nyomtatás a beépülő
      // nézőben fut); egy idő után mindenképp takarítunk.
      setTimeout(cleanup, PRINT_FRAME_MAX_MS);
    } catch {
      // Egy böngésző a beépített nézőjének keretét elzárhatja előlünk. A
      // nyomtatás ilyenkor elmarad, de a keret és a blob nem ragad bent.
      cleanup();
    }
  });

  if (!type.startsWith('image/')) {
    frame.src = url;
  }
  document.body.append(frame);
}

/**
 * EGY LAP, RAJTA A KÉP — egy A4-es lapra illesztve, arányosan.
 *
 * MILLIMÉTERBEN, NEM `vh`-ban. A keret 0 × 0 px-es, és a nézethez kötött
 * egység (`vh`, `%` magasság) a keretben ehhez mérhet — a kép nullára
 * zsugorodna, és üres lap jönne ki. A 277 × 190 mm az A4 a 10 mm-es margón
 * belül; a magyar irodában ez a papír. A cím a kódunkból jön (`blob:`), a
 * fájl tartalma nem kerül a HTML-be. A keret `load`-ja a kép betöltése UTÁN
 * sül el, így a nyomtatás nem üres képet kap.
 */
export function imagePrintPage(url: string): string {
  return [
    '<!doctype html><html><head><meta charset="utf-8"><style>',
    '@page{size:A4;margin:10mm}',
    'html,body{margin:0}',
    'img{display:block;margin:0 auto;max-width:190mm;max-height:277mm;object-fit:contain}',
    '</style></head><body>',
    `<img src="${url}" alt="">`,
    '</body></html>',
  ].join('');
}
